import { HTMLAttributes } from 'react'

import Container from '@/components/base/container'
import CtaButton from '@/components/base/cta-button'
import Media, { hasMedia } from '@/components/base/media'
import Motion from '@/components/base/motion'
import ParallaxImage from '@/components/base/parallax-image'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { editorialFullscreenRevealAnimation } from '@/components/sections/animations/editorial-fullscreen'

import { cn } from '@/lib/utils'
import { WPLink, WPMedia } from '@/types'

export type EditorialFullscreenProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    type: 'editorial' | 'concept'
    pretitle: string
    title: string
    media: WPMedia
    text: string
    link_1: WPLink
    link_2: WPLink
  }>

const EditorialFullscreen = ({
  type = 'concept',
  pretitle,
  title,
  media,
  text,
  link_1,
  link_2,
  className,
  ...props
}: EditorialFullscreenProps) => {
  const hasImage = (media && hasMedia(media)) || false

  return (
    <Motion animation={editorialFullscreenRevealAnimation}>
      {(motionRef) => (
        <Section
          ref={motionRef}
          className={cn('relative min-h-[calc(var(--vh)*100)]', className)}
          {...props}
          data-type={type}
        >
          {pretitle || title || text || link_1?.url || link_2?.url ? (
            <Container className="relative z-10 min-h-[calc(var(--vh)*100)]">
              <div
                className={cn(
                  'flex min-h-[calc(var(--vh)*100)] flex-col justify-center gap-y-spacing-md py-[40px] md:gap-y-spacing-lg md:py-[100px]',
                  {
                    'justify-between': type === 'concept'
                  }
                )}
              >
                <div
                  className={cn(
                    'flex w-auto flex-col gap-y-spacing-md md:w-[40%]',
                    {
                      'gap-y-spacing-xs': type === 'concept'
                    }
                  )}
                >
                  {pretitle ? (
                    <Typography
                      variant="large"
                      className={cn(
                        'condensed',
                        '!text-text-primary-dark dark:!text-text-primary-dark',
                        {
                          '!text-text-primary-light': !hasImage
                        }
                      )}
                      data-motion-pretitle
                      component="p"
                    >
                      {pretitle}
                    </Typography>
                  ) : null}

                  {title ? (
                    <Typography
                      variant="h3"
                      component="h2"
                      data-motion-title
                      className={cn(
                        'text-s-h3 md:text-d-h2',
                        '!text-text-heading-dark dark:!text-text-heading-dark',
                        {
                          '!text-text-heading-light': !hasImage
                        }
                      )}
                    >
                      {title}
                    </Typography>
                  ) : null}
                </div>

                <div
                  className={cn(
                    'flex w-full flex-col items-start gap-y-spacing-md md:w-[40%]',
                    {
                      'justify-between md:w-full md:flex-row-reverse md:items-end md:gap-0':
                        type === 'concept'
                    }
                  )}
                >
                  <div
                    className={cn({
                      'flex-[1_1_auto] md:flex-[0_0_31.5%]': type === 'concept'
                    })}
                  >
                    {text ? (
                      <Typography
                        variant="h6"
                        paragraph
                        data-motion-text
                        className={cn(
                          'text-regular md:text-large !text-text-primary-dark',
                          {
                            '!text-text-primary-light': !hasImage
                          }
                        )}
                      >
                        {text}
                      </Typography>
                    ) : null}
                  </div>

                  <div
                    className={cn({
                      'flex-[1_1_auto] md:flex-[0_0_50%]': type === 'concept'
                    })}
                    data-motion-ctas
                  >
                    <div className="flex flex-wrap gap-spacing-2xs place-self-start">
                      {link_1?.url ? (
                        <CtaButton
                          href={link_1.url}
                          target={link_1.target}
                          variant="primary"
                          typography="regular"
                          className={cn('dark min-w-[250px] md:min-w-0')}
                          data-motion-button
                        >
                          {link_1.title}
                        </CtaButton>
                      ) : null}

                      {link_2?.url ? (
                        <CtaButton
                          href={link_2.url}
                          target={link_2.target}
                          variant="secondary"
                          typography="regular"
                          className={cn('dark min-w-[250px] md:min-w-0')}
                        >
                          {link_2.title}
                        </CtaButton>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </Container>
          ) : null}
          {(media?.poster?.url || media?.video?.url) && (
            <div className="absolute inset-0 z-0" data-motion-media>
              <ParallaxImage
                offset={-40}
                className="absolute inset-0 h-full w-full"
              >
                <>
                  <div
                    className={cn(
                      'pointer-events-none absolute inset-0 z-20 flex items-center justify-center',
                      {
                        'bg-[black]/40':
                          pretitle ||
                          title ||
                          text ||
                          link_1?.url ||
                          link_2?.url
                      }
                    )}
                  />
                  <Media
                    media={media}
                    data-lightbox={
                      !pretitle &&
                      !title &&
                      !text &&
                      !link_1?.url &&
                      !link_2?.url
                        ? 'true'
                        : undefined
                    }
                    className={cn('absolute left-0 top-0 z-0 h-full w-full')}
                  />
                </>
              </ParallaxImage>
              {/* <Media
                media={media}
                data-lightbox={
                  !pretitle && !title && !text && !link_1?.url && !link_2?.url
                    ? 'true'
                    : undefined
                }
                className={cn('absolute left-0 top-0 h-full w-full', {
                  'opacity-60':
                    pretitle || title || text || link_1?.url || link_2?.url
                })}
              /> */}
            </div>
          )}
        </Section>
      )}
    </Motion>
  )
}

export default EditorialFullscreen
